// src/utils/compareCharacters.js

export function compareCharacters(guess, answer) {
  const feedback = {
    biographical_info: {},
    physical_description: {},
    gameplay_details: {},
  };

  // Compare biographical info
  feedback.biographical_info.Home = compareValues(
    guess.biographical_info.Home,
    answer.biographical_info.Home
  );
  feedback.biographical_info.Age = compareValues(
    guess.biographical_info.Age,
    answer.biographical_info.Age,
    'number' // Indicate that we're comparing numbers
  );
  feedback.biographical_info['First Appearance'] = compareValues(
    guess.biographical_info['First Appearance'],
    answer.biographical_info['First Appearance'],
    'number' // Indicate that we're comparing numbers
  );
  feedback.biographical_info.Affiliation = compareValues(
    guess.biographical_info.Affiliation,
    answer.biographical_info.Affiliation
  );
  feedback.biographical_info.Education = compareValues(
    guess.biographical_info.Education,
    answer.biographical_info.Education
  );

  // Compare physical description
  feedback.physical_description.Race = compareValues(
    guess.physical_description.Race,
    answer.physical_description.Race
  );
  feedback.physical_description.Gender = compareValues(
    guess.physical_description.Gender,
    answer.physical_description.Gender
  );
  feedback.physical_description['Hair color'] = compareValues(
    guess.physical_description['Hair color'],
    answer.physical_description['Hair color']
  );
  feedback.physical_description['Eye color'] = compareValues(
    guess.physical_description['Eye color'],
    answer.physical_description['Eye color']
  );

  // Compare gameplay details
  feedback.gameplay_details.Type = compareValues(
    guess.gameplay_details.Type,
    answer.gameplay_details.Type
  );
  feedback.gameplay_details['Job class'] = compareValues(
    guess.gameplay_details['Job class'],
    answer.gameplay_details['Job class']
  );

  return feedback;
}

function compareValues(guessValue, answerValue, valueType = 'default') {
  // Check if the answerValue is missing or undefined
  if (
    answerValue === undefined ||
    answerValue === null ||
    answerValue === '-'
  ) {
    // Return a special result indicating the attribute is unknown in the answer
    return {
      result: 'Unknown',
      guessValue: formatValue(guessValue),
    };
  }

  // Normalize values to arrays
  const guessValues = Array.isArray(guessValue) ? guessValue : [guessValue];
  const answerValues = Array.isArray(answerValue)
    ? answerValue
    : [answerValue];

  // Filter out undefined or null values
  const sanitizedGuessValues = guessValues.filter(
    (v) => v !== undefined && v !== null
  );
  const sanitizedAnswerValues = answerValues.filter(
    (v) => v !== undefined && v !== null
  );

  // Compare numbers for specific value types
  if (valueType === 'number') {
    const guessNum = parseFloat(sanitizedGuessValues[0]);
    const answerNum = parseFloat(sanitizedAnswerValues[0]);

    if (!isNaN(guessNum) && !isNaN(answerNum)) {
      if (guessNum === answerNum) {
        return {
          result: 'Match',
          guessValue: formatValue(guessValue),
        };
      } else if (guessNum > answerNum) {
        return {
          result: 'More',
          guessValue: formatValue(guessValue),
        };
      } else {
        return {
          result: 'Less',
          guessValue: formatValue(guessValue),
        };
      }
    }
  }

  // Convert all values to lowercase strings for case-insensitive comparison
  const lowerGuessValues = sanitizedGuessValues.map((v) =>
    v.toString().toLowerCase()
  );
  const lowerAnswerValues = sanitizedAnswerValues.map((v) =>
    v.toString().toLowerCase()
  );

  // Find the intersection
  const intersection = lowerGuessValues.filter((value) =>
    lowerAnswerValues.includes(value)
  );

  // Determine the comparison result based on the intersection
  if (
    intersection.length === lowerGuessValues.length &&
    intersection.length === lowerAnswerValues.length &&
    intersection.length > 0
  ) {
    // Both guess and answer values are identical
    return {
      result: 'Match',
      guessValue: formatValue(guessValue),
    };
  } else if (intersection.length > 0) {
    // Some elements match but not all
    return {
      result: 'Partial Match',
      guessValue: formatValue(guessValue),
    };
  } else {
    // No elements match
    return {
      result: 'No Match',
      guessValue: formatValue(guessValue),
    };
  }
}

function extractNumber(value) {
  if (typeof value !== 'string') {
    if (value !== undefined && value !== null) {
      value = String(value);
    } else {
      return null;
    }
  }
  const match = value.match(/(\d+(\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
}

function formatValue(value) {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return value !== undefined && value !== null ? value.toString() : '-';
}
